import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoBox } from "../../Elements/Alert/InfoBox";
import { Section } from "../../Components/Section";
import {
  Button,
  ITableColumn,
  ITableDataItem,
  Tooltip,
} from "@inceptionbg/ui-components";
import { FieldBookDialog } from "./FieldBookDialog";
import { IFieldBook } from "../../types/IFieldBook";
import {
  deleteField,
  getFieldBooks,
  saveFieldBook,
} from "../../repos/FIeldBookRepo";

import { FarmContext } from "../../AppContent";
import { SurfaceValue } from "../../Components/Inputs/Surface/SurfaceValue";
import { convertSurfaceToObj } from "../../utils/CalculateUtils";
import { Table } from "../../Components/CustomIUI/Table/Table";
import { SubSection } from "../../Components/SubSection";
import { Loader } from "../../Elements/Loader";
import { Dialog } from "../../Elements/Dialog";

export const FieldsBookPage: FC = () => {
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [fieldBook, setFieldBook] = useState<Partial<IFieldBook>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<IFieldBook[]>([]);
  const [fieldToDeleteUuid, setFieldToDeleteUuid] = useState("");

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;

  const handleGetFieldBooks = useCallback(() => {
    if (farmUuid) {
      setIsLoading(true);
      getFieldBooks(farmUuid, ["FIELD_LANDS"])
        .then(({ fields }) => setFields(fields || []))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [farmUuid]);

  useEffect(handleGetFieldBooks, [handleGetFieldBooks]);

  const columns: ITableColumn[] = [
    { id: "name", label: t("Field") },
    { id: "surface", label: t("SurfaceTitleFormatted"), align: "right" },
    { id: "delete", label: "", align: "center" },
  ];

  const handleDeleteField = () => {
    if (!fieldToDeleteUuid) return;

    setIsLoading(true);
    deleteField(fieldToDeleteUuid)
      .then(() => {
        setFieldToDeleteUuid("");
        handleGetFieldBooks();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const data: ITableDataItem[] = fields.map((field) => {
    const totalSurface = field.fieldLands
      ? field.fieldLands.reduce(
          (sum, fl) => sum + (fl.land?.usedSurface || 0),
          0
        )
      : 0;

    return {
      uuid: field.uuid!,
      onRowClick: () => window.open(`/fields-book/${field.uuid}`, "_blank"),
      cells: {
        name: { value: field.name },
        surface: {
          value: (
            <SurfaceValue
              valueObj={convertSurfaceToObj(totalSurface || 0)}
              simple={true}
            />
          ),
        },
        delete: {
          value: (
            <Tooltip
              label={
                field.deletingAllowed ? "" : t("DeleteFieldBlockedMessage")
              }
              position="top"
            >
              <Button
                label={t("Delete")}
                size="s"
                outlined
                onClick={(e) => {
                  e.stopPropagation();
                  setFieldToDeleteUuid(field.uuid);
                }}
                disabled={!field.deletingAllowed || isLoading}
              />
            </Tooltip>
          ),
        },
      },
    };
  });
  const onSubmit = () => {
    setIsLoading(true);
    saveFieldBook({ ...fieldBook, farm: { uuid: farmUuid } })
      .then(() => {
        handleGetFieldBooks();
        setIsOpenForm(false);
        setFieldBook({});
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  return (
    <Section title={t("FieldsBook")}>
      <InfoBox
        title={t("FieldsBookStatementTitle")}
        desc={t("FieldsBookStatementDesc")}
        className="ml-4 mr-4 mb-4 mt-0"
        light
      />
      <Loader isLoading={isLoading}>
        <>
          <div className="flex justify-right">
            <Tooltip
              className="field-tooltip"
              label={t("AddNewFieldTooltip")}
              position="top"
            >
              <Button
                label={t("AddNewField")}
                size="s"
                primary
                onClick={() => setIsOpenForm(true)}
              />
            </Tooltip>
          </div>

          <Table
            headerWrap
            data={data}
            columns={columns}
            hideFooter
            customPagination={{ defaultLimit: 1000 }}
            className="table-5col mb-3"
          />

          <SubSection title={t("Reports")}>
            <Button
              label={t("FieldEntryReport")}
              size="s"
              outlined
              onClick={() => window.open(`/fields-book/report`, "_blank")}
            />
          </SubSection>
        </>
      </Loader>

      <FieldBookDialog
        isOpen={isOpenForm}
        onClose={() => {
          setIsOpenForm(false);
          setFieldBook({});
        }}
        data={fieldBook}
        setData={setFieldBook}
        onSubmit={onSubmit}
      />
      <Dialog
        isOpen={!!fieldToDeleteUuid}
        onClose={() => setFieldToDeleteUuid("")}
        title={t("DeleteField")}
        desc={t("DeleteFieldDesc")}
        isLoading={isLoading}
        confirmButton={{ label: t("Confirm"), onClick: handleDeleteField }}
        cancelButton={{ onClick: () => setFieldToDeleteUuid("") }}
        error
      />
    </Section>
  );
};
